import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFulfilments, addFulfilment, removeFulfilment, updateClinic } from '../../../../apis/clinics/requests';
import { Fulfilment, AddFulfilmentRequest, FulfilmentTypeEnum } from '../../../../apis/clinics/types';
import { useSnackbar } from 'notistack';

interface UseFulfilmentsReturn {
  fulfilments: Fulfilment[];
  totalCount: number;
  isLoading: boolean;
  addFulfilment: (params: AddFulfilmentRequest) => Promise<Fulfilment>;
  removeFulfilment: (fulfilmentId: string) => Promise<void>;
  updateFulfilmentType: (fulfilmentType: FulfilmentTypeEnum | null) => Promise<void>;
}

const useFulfilments = (clinicId: string, page: number, limit: number): UseFulfilmentsReturn => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading } = useQuery(
    ['fulfilments', clinicId, page, limit],
    () => getFulfilments(clinicId, { page, limit }),
  );

  const addMutation = useMutation(
    (params: AddFulfilmentRequest) => addFulfilment(clinicId, params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fulfilments', clinicId]);
        enqueueSnackbar('Fulfilment has been added', { variant: 'success' });
      }
    }
  );

  const removeMutation = useMutation(
    (fulfilmentId: string) => removeFulfilment(clinicId, fulfilmentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fulfilments', clinicId]);
        enqueueSnackbar('Fulfilment has been removed', { variant: 'success' });
      }
    }
  );

  const updateFulfilmentTypeMutation = useMutation(
    (fulfilmentType: FulfilmentTypeEnum | null) => {
      return updateClinic(clinicId, { fulfilment_type: fulfilmentType });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clinic', clinicId]);
        enqueueSnackbar('Fulfilment type has been updated', { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar('Failed to update fulfilment type', { variant: 'error' });
      }
    }
  );

  return {
    fulfilments: data?.data || [],
    totalCount: data?.pagination?.total || 0,
    isLoading,
    addFulfilment: addMutation.mutateAsync,
    removeFulfilment: removeMutation.mutateAsync,
    updateFulfilmentType: updateFulfilmentTypeMutation.mutateAsync
  };
};

export default useFulfilments; 
